import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebase";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch, useSelector } from "react-redux";
import { handlePostRequest } from "../../services/PostTemplate";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import BatchScanner from "../../components/scanning/BatchScanner";
import ReceiverSelect from "../../components/customer/ReceiverSelect";
import PaymentAllocationPanel from "../../components/payment/PaymentAllocationPanel";
import { generateIdempotencyKey } from "../../services/clientPaymentService";
import { fetchCashRegisters } from "../../services/cashService";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "./inStorePickup.module.scss";
import { checkInvoiceConflicts } from "../../services/invoiceConflictService";
import InvoiceConflictDialog from "../../components/InvoiceConflictDialog";

const STATUS_PILL_CLASS = {
    Scanning: "pillScanning",
    AwaitingPayment: "pillAwaitingPayment",
    AwaitingReceiver: "pillAwaitingReceiver",
    AwaitingApproval: "pillAwaitingApproval",
    Completed: "pillCompleted",
};

const STATUS_LABEL = {
    AwaitingReceiver: "Paid",
};

const STEPS = [
    { key: "scan", label: "Scan" },
    { key: "payment", label: "Verify/Pay" },
    { key: "receiver", label: "Receiver" },
    { key: "complete", label: "Complete" },
];

// "bankTransfer" is kept for historical pickups recorded by the old payPickup
// flow; new payments are recorded as "wire" - see models/inStorePickup.js.
const PAYMENT_METHOD_LABELS = { cash: "Cash", bankTransfer: "Wire Transfer", wire: "Wire Transfer", card: "Card" };

const METHOD_CARDS = [
    { value: "cash", label: "Cash", icon: "pi pi-money-bill" },
    { value: "wire", label: "Wire Transfer", icon: "pi pi-building" },
    { value: "card", label: "Card", icon: "pi pi-credit-card" },
];

const RECEIVER_TYPE_LABELS = { client: "Client", agent: "Agent", guest: "Not a customer" };

const uploadDataUrl = async (dataUrl, path) => {
    const blob = await fetch(dataUrl).then((r) => r.blob());
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob);
    return getDownloadURL(storageRef);
};

const InStorePickupDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const isAdmin = localStorage.getItem("role") === "admin";
    const { shift } = useSelector((s) => s.activeShiftSlice);
    const viewOnly = useViewOnly();

    const [pickup, setPickup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState("scan");
    const [scannedPackages, setScannedPackages] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [uninvoicedPackages, setUninvoicedPackages] = useState([]);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [invoiceConflicts, setInvoiceConflicts] = useState([]);
    const [showInvoiceConflicts, setShowInvoiceConflicts] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey());
    const [panelKey, setPanelKey] = useState(0);

    // Only relevant for cash: whoever is paying might not have a register of
    // their own to fall back on (admin never has a shift here at all; a
    // clerk/handler can be on a registerless "remote" shift) - in that case
    // the backend requires picking one explicitly, so this loads the list up
    // front rather than only after a cash attempt already failed.
    const [cashRegisters, setCashRegisters] = useState([]);
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const needsCashRegisterPicker = !shift?.cashRegister;

    const [overrideReason, setOverrideReason] = useState("");

    const [receiverState, setReceiverState] = useState({ receiver: null, signatureDataUrl: null, isValid: false });

    const initialStepFor = (p) => {
        if (!p) return "scan";
        if (p.status === "Completed") return "complete";
        if (p.status === "AwaitingApproval") return isAdmin ? "payment" : "waiting";
        if (p.status === "AwaitingPayment") return "payment";
        if (p.status === "AwaitingReceiver") return "receiver";
        if (p.scannedPackageIds?.length) return "payment";
        return "scan";
    };

    const load = useCallback(async () => {
        setLoading(true);
        const res = await handleGetRequest(`/in-store-pickup/${id}`);
        const p = res?.data || null;
        setPickup(p);
        setStep(initialStepFor(p));
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const refreshSilently = useCallback(async () => {
        const res = await handleGetRequest(`/in-store-pickup/${id}`);
        const p = res?.data || null;
        setPickup(p);
        if (p?.override?.status === "approved" && step === "waiting") {
            setStep("payment");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, step]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!needsCashRegisterPicker) return;
        fetchCashRegisters().then((res) => setCashRegisters(res?.data || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsCashRegisterPicker]);
    const cashRegisterOptions = cashRegisters.map((r) => ({
        label: r.inUseBy ? `${r.name} (currently held by ${r.inUseBy})` : r.name,
        value: r._id,
    }));

    // Mirror of the picker gate above: when the current shift already has a
    // register, attach it automatically instead of leaving cashRegisterId
    // null - the panel now always requires a value for cash payments.
    useEffect(() => {
        if (needsCashRegisterPicker) return;
        const myRegisterId = shift?.cashRegister?._id || (typeof shift?.cashRegister === "string" ? shift.cashRegister : null);
        if (myRegisterId) setCashRegisterId(myRegisterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsCashRegisterPicker, shift?.cashRegister]);

    // Poll while waiting on an admin to approve an override, so a non-admin
    // clerk's screen advances automatically once it's decided.
    useEffect(() => {
        if (step !== "waiting") return;
        const interval = setInterval(refreshSilently, 4000);
        return () => clearInterval(interval);
    }, [step, refreshSilently]);

    const handleDeletePickup = () => {
        confirmDialog({
            message: `Delete pickup ${pickup.pickupId}? This can't be undone. Any packages/payments it already recorded stay as-is - only the pickup record itself is removed.`,
            header: "Delete Pickup",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    const response = await axios.delete(`${DEV}/in-store-pickup/${id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (response?.data?.success) {
                        toast.success("Pickup deleted");
                        history.push("/in-store-pickups");
                    } else {
                        toast.error(response?.data?.message || "Failed to delete pickup");
                    }
                } catch (error) {
                    console.error("Error deleting pickup:", error);
                    toast.error(error?.response?.data?.message || "Failed to delete pickup");
                }
            },
        });
    };

    const handleScanSubmit = async () => {
        if (scannedPackages.length === 0) {
            if (pickup?.scannedPackageIds?.length) {
                setStep("payment");
                return;
            }
            toast.error("Scan at least one package first");
            return;
        }
        setSubmitting(true);
        try {
            const res = await dispatch(
                handlePostRequest({ packageIds: scannedPackages.map((p) => p._id) }, `/in-store-pickup/${id}/scan`, false, false),
            );
            if (res === "error" || !res?.success) {
                toast.error(res?.message || "Failed to scan packages");
                return;
            }
            if (res.data.rejected?.length) {
                res.data.rejected.forEach((r) => toast.warn(`${r.id}: ${r.reason}`));
            }
            if (!res.data.addedCount && !pickup?.scannedPackageIds?.length) {
                toast.error("None of the scanned packages could be added");
                return;
            }
            setScannedPackages([]);
            await load();
            setStep("payment");
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerify = async () => {
        setSubmitting(true);
        try {
            const res = await dispatch(handlePostRequest({}, `/in-store-pickup/${id}/verify`, false, true));
            if (res === "error" || !res?.success) return;
            setPickup(res.data.pickup);
            setUnpaidInvoices(res.data.unpaidInvoices || []);
            setUninvoicedPackages(res.data.uninvoicedPackages || []);
            if (!res.data.unpaidInvoices?.length && !res.data.uninvoicedPackages?.length) {
                toast.success("Fully paid - continue to receiver");
                setStep("receiver");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const runGenerateInvoice = async () => {
        setGeneratingInvoice(true);
        try {
            const res = await dispatch(
                handlePostRequest({}, `/in-store-pickup/${id}/generate-invoice`, true, true),
            );
            if (res === "error" || !res?.success) return;
            toast.success("Invoice generated");
            await handleVerify();
        } finally {
            setGeneratingInvoice(false);
        }
    };

    const handleGenerateInvoice = async () => {
        const waybills = uninvoicedPackages.map((pkg) => pkg.Waybill).filter(Boolean);

        setGeneratingInvoice(true);
        let foundConflicts;
        try {
            foundConflicts = await checkInvoiceConflicts(waybills);
        } finally {
            setGeneratingInvoice(false);
        }

        if (foundConflicts.length > 0) {
            setInvoiceConflicts(foundConflicts);
            setShowInvoiceConflicts(true);
            return;
        }
        await runGenerateInvoice();
    };

    // The backend derives which packages to invoice itself and always skips
    // ones already invoiced (see utils/common/invoiceGuard.js), so there's no
    // separate "remove conflicting and continue" request to make here -
    // proceeding just re-runs the same generation, which is guaranteed safe.
    const handleProceedDespiteConflicts = async () => {
        setShowInvoiceConflicts(false);
        await runGenerateInvoice();
    };

    const handleCancelInvoiceConflicts = () => {
        setShowInvoiceConflicts(false);
        setInvoiceConflicts([]);
    };

    // Cash/Wire commit immediately against this pickup's own unpaid invoices
    // via the bulk-payment engine. Card never records anything itself here -
    // it stages the plan the panel computed and hands off to the real
    // gateway on /payment, which calls pay-card-commit once PayPal confirms
    // what was actually captured.
    const handleAllocationAction = async (snapshot) => {
        if (selectedMethod === "card") {
            localStorage.setItem(
                "pickupCardPlan",
                JSON.stringify({
                    pickupId: id,
                    mode: snapshot.mode,
                    amountReceived: snapshot.amountReceived,
                    selections: snapshot.selections || null,
                }),
            );
            localStorage.setItem("returnTo", `/in-store-pickup/${id}`);
            history.push("/payment");
            return;
        }

        const methodLabel = METHOD_CARDS.find((m) => m.value === selectedMethod)?.label || selectedMethod;
        const amountLabel = snapshot.currency === "jmd" ? `J$${Number(snapshot.amountReceived).toFixed(2)}` : `$${snapshot.amountReceived.toFixed(2)}`;
        confirmDialog({
            message: `Record a ${methodLabel} payment of ${amountLabel} against this pickup?`,
            header: "Confirm Payment",
            icon: "pi pi-question-circle",
            accept: async () => {
                setBulkSubmitting(true);
                try {
                    const res = await dispatch(
                        handlePostRequest(
                            {
                                idempotencyKey,
                                mode: snapshot.mode,
                                amountReceived: snapshot.amountReceived,
                                currency: snapshot.currency,
                                paymentMethod: selectedMethod,
                                ...(snapshot.selections ? { selections: snapshot.selections } : {}),
                                ...(selectedMethod === "cash" ? { cashRegisterId: snapshot.cashRegisterId } : {}),
                            },
                            `/in-store-pickup/${id}/pay-bulk`,
                            true,
                            true,
                        ),
                    );
                    if (res === "error" || !res?.success) return;
                    toast.success("Payment recorded");
                    setSelectedMethod(null);
                    setPanelKey((k) => k + 1);
                    setIdempotencyKey(generateIdempotencyKey());
                    await handleVerify();
                } finally {
                    setBulkSubmitting(false);
                }
            },
        });
    };

    const handleRequestOverride = async () => {
        if (!overrideReason.trim()) {
            toast.error("Enter a reason for the override request");
            return;
        }
        setSubmitting(true);
        try {
            const res = await dispatch(
                handlePostRequest({ reason: overrideReason.trim() }, `/in-store-pickup/${id}/override/request`, true, true),
            );
            if (res === "error" || !res?.success) return;
            toast.success("Override requested - waiting on an admin");
            setOverrideReason("");
            await load();
        } finally {
            setSubmitting(false);
        }
    };

    const handleApproveOverride = async () => {
        if (!pickup?.override?.reason && !overrideReason.trim()) {
            toast.error("Enter a reason for the override");
            return;
        }
        setSubmitting(true);
        try {
            const res = await dispatch(
                handlePostRequest({ reason: overrideReason.trim() }, `/in-store-pickup/${id}/override/approve`, true, true),
            );
            if (res === "error" || !res?.success) return;
            toast.success("Override approved");
            setOverrideReason("");
            await load();
            setStep("receiver");
        } finally {
            setSubmitting(false);
        }
    };

    const handleComplete = async () => {
        if (!receiverState.isValid) {
            toast.error("Select a receiver and capture their signature");
            return;
        }
        setSubmitting(true);
        try {
            const signatureUrl = await uploadDataUrl(receiverState.signatureDataUrl, `inStorePickups/${id}/signature_${Date.now()}.png`);
            const res = await dispatch(
                handlePostRequest({ receiver: receiverState.receiver, signatureUrl }, `/in-store-pickup/${id}/complete`, true, true),
            );
            if (res === "error" || !res?.success) return;
            toast.success("Pickup completed");
            await load();
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading pickup...</span>
                </div>
            </div>
        );
    }

    if (!pickup) {
        return (
            <div className={styles.page}>
                <div className={styles.notFoundState}>
                    <i className="pi pi-exclamation-circle" />
                    <span>Pickup not found</span>
                </div>
            </div>
        );
    }

    const stepIndexOf = (key) => STEPS.findIndex((s) => s.key === key);
    const currentStepIndex = step === "waiting" ? stepIndexOf("payment") : stepIndexOf(step);
    const clientName = pickup.clientId ? `${pickup.clientId.firstName} ${pickup.clientId.lastName}` : null;

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <span className={styles.backLink} onClick={() => history.push("/in-store-pickups")}>
                <i className="pi pi-arrow-left" />
                Back to In-Store Pickups
            </span>

            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className="pi pi-shopping-bag" />
                        </div>
                        <div>
                            <h2>{pickup.pickupId}</h2>
                            <p className={styles.subtitle}>{clientName || "No client scanned yet"}</p>
                        </div>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <span className={`${styles.pill} ${styles[STATUS_PILL_CLASS[pickup.status]]}`}>{STATUS_LABEL[pickup.status] || pickup.status}</span>
                    {isAdmin && pickup.status !== "Completed" && (
                        <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={handleDeletePickup} tooltip="Delete pickup" />
                    )}
                </div>
            </div>

            {pickup.status !== "Completed" && (
                <div className={styles.stepsNav}>
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.key}>
                            <span className={`${styles.stepPill} ${i === currentStepIndex ? styles.stepActive : i < currentStepIndex ? styles.stepDone : ""}`}>
                                <span className={styles.stepNum}>{i < currentStepIndex ? <i className="pi pi-check" /> : i + 1}</span>
                                {s.label}
                            </span>
                            {i < STEPS.length - 1 && <i className={`pi pi-chevron-right ${styles.stepArrow}`} />}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {step === "scan" && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-qrcode" />
                            Scan Packages
                        </h3>
                    </div>
                    <BatchScanner
                        scannableEndpoint="/in-store-pickup/scannable-packages"
                        lookupEndpoint="/in-store-pickup/package-lookup"
                        enforceSingleClient
                        onScannedChange={setScannedPackages}
                    />
                    <div className={styles.navRow}>
                        <span />
                        <Button
                            label={submitting ? "Submitting..." : "Continue"}
                            icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-arrow-right"}
                            className={styles.primaryButton}
                            disabled={submitting || viewOnly}
                            onClick={handleScanSubmit}
                        />
                    </div>
                </div>
            )}

            {step === "payment" && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-wallet" />
                            Verify Payment
                        </h3>
                        <Button label="Add More Packages" icon="pi pi-plus" text onClick={() => setStep("scan")} />
                    </div>

                    <div className={styles.scannedList}>
                        {(pickup.scannedPackageIds || []).map((pkg) => (
                            <div key={pkg._id} className={styles.scannedItem}>
                                <strong>{pkg.Waybill}</strong>
                                <span>{pkg.packageStatus}</span>
                            </div>
                        ))}
                    </div>

                    {(() => {
                        const effectiveUninvoiced = uninvoicedPackages.length ? uninvoicedPackages : pickup.uninvoicedPackageIds || [];
                        const effectiveUnpaidInvoices = unpaidInvoices.length ? unpaidInvoices : pickup.unpaidInvoiceIds || [];
                        const nothingCheckedYet = !effectiveUnpaidInvoices.length && !effectiveUninvoiced.length;

                        if (nothingCheckedYet) {
                            return (
                                <div className={styles.navRow}>
                                    <span />
                                    <Button
                                        label={submitting ? "Checking..." : "Check Payment Status"}
                                        icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-search"}
                                        className={styles.primaryButton}
                                        disabled={submitting || viewOnly}
                                        onClick={handleVerify}
                                    />
                                </div>
                            );
                        }

                        return (
                            <>
                                {effectiveUninvoiced.length > 0 && (
                                    <div className={styles.amountBanner}>
                                        <div className={styles.amountIcon}>
                                            <i className="pi pi-file-excel" />
                                        </div>
                                        <div>
                                            <div className={styles.amountValue}>{effectiveUninvoiced.length} package(s)</div>
                                            <div className={styles.amountLabel}>No Invoice Generated Yet</div>
                                        </div>
                                    </div>
                                )}

                                {effectiveUninvoiced.length > 0 && (
                                    <div className={styles.invoiceList}>
                                        {effectiveUninvoiced.map((pkg) => (
                                            <div key={pkg._id} className={styles.invoiceRow}>
                                                <strong>{pkg.Waybill}</strong>
                                                <span>Not invoiced</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {effectiveUninvoiced.length > 0 && (
                                    <div className={styles.navRow}>
                                        {isAdmin ? (
                                            <>
                                                <span />
                                                <Button
                                                    label={generatingInvoice ? "Generating..." : "Generate Invoice"}
                                                    icon={generatingInvoice ? "pi pi-spin pi-spinner" : "pi pi-file-edit"}
                                                    className={styles.primaryButton}
                                                    disabled={generatingInvoice}
                                                    onClick={handleGenerateInvoice}
                                                />
                                            </>
                                        ) : (
                                            <span style={{ fontSize: "13px", color: "#b91c1c" }}>
                                                Ask an admin to generate an invoice for these packages before payment can be collected.
                                            </span>
                                        )}
                                    </div>
                                )}

                                {effectiveUnpaidInvoices.length > 0 && (
                                    <>
                                        <div className={styles.amountBanner}>
                                            <div className={styles.amountIcon}>
                                                <i className="pi pi-exclamation-triangle" />
                                            </div>
                                            <div>
                                                <div className={styles.amountValue}>${(pickup.amountDue || 0).toFixed(2)}</div>
                                                <div className={styles.amountLabel}>Amount Due</div>
                                            </div>
                                        </div>

                                        <div className={styles.invoiceList}>
                                            {effectiveUnpaidInvoices.map((inv) => (
                                                <div key={inv._id} className={styles.invoiceRow}>
                                                    <strong>{inv.invoiceId || inv._id}</strong>
                                                    <span>${(inv.invoiceAmount || 0).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                            {effectiveUnpaidInvoices.length > 0 && (
                                <>
                                    {!selectedMethod ? (
                                        <>
                                            <div className={styles.paymentMethods}>
                                                {METHOD_CARDS.map((m) => (
                                                    <button key={m.value} type="button" className={styles.methodCard} onClick={() => setSelectedMethod(m.value)}>
                                                        <i className={m.icon} />
                                                        <strong>{m.label}</strong>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className={styles.navRow}>
                                                <Button label="Refresh Payment Status" icon="pi pi-refresh" outlined disabled={submitting || viewOnly} onClick={handleVerify} />
                                                <span />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className={styles.navRow} style={{ marginTop: 0, marginBottom: "14px" }}>
                                                <Button
                                                    label={`Paying by ${METHOD_CARDS.find((m) => m.value === selectedMethod)?.label}`}
                                                    icon="pi pi-arrow-left"
                                                    text
                                                    onClick={() => setSelectedMethod(null)}
                                                />
                                                <Button label="Refresh Payment Status" icon="pi pi-refresh" outlined disabled={submitting || bulkSubmitting || viewOnly} onClick={handleVerify} />
                                            </div>
                                            <PaymentAllocationPanel
                                                key={panelKey}
                                                invoices={effectiveUnpaidInvoices}
                                                totalOutstanding={pickup.amountDue}
                                                accountCreditBalance={0}
                                                paymentMethod={selectedMethod}
                                                {...(selectedMethod === "cash"
                                                    ? {
                                                          cashRegisterId,
                                                          onCashRegisterIdChange: setCashRegisterId,
                                                          ...(needsCashRegisterPicker
                                                              ? { cashRegisterOptions }
                                                              : { lockedCashRegisterLabel: shift?.cashRegister?.name }),
                                                      }
                                                    : {})}
                                                actionLabel={selectedMethod === "card" ? "Continue to Card Payment" : "Record Payment"}
                                                actionIcon={selectedMethod === "card" ? "pi pi-arrow-right" : "pi pi-check"}
                                                onAction={handleAllocationAction}
                                                submitting={bulkSubmitting}
                                                actionDisabled={viewOnly}
                                                hideStats
                                            />
                                        </>
                                    )}
                                </>
                            )}

                            <div className={styles.overridePanel}>
                                {isAdmin ? (
                                    <>
                                        <h4>
                                            <i className="pi pi-shield" />
                                            Admin Override
                                        </h4>
                                        {pickup.override?.status === "requested" ? (
                                            <p style={{ fontSize: "13px", color: "#92400e" }}>
                                                Requested by {pickup.override.requestedBy?.firstName} {pickup.override.requestedBy?.lastName}: "{pickup.override.reason}"
                                            </p>
                                        ) : (
                                            <textarea placeholder="Reason for overriding the unpaid balance..." value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
                                        )}
                                        <Button
                                            label={submitting ? "Approving..." : "Approve Override"}
                                            icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                            severity="warning"
                                            disabled={submitting || viewOnly}
                                            onClick={handleApproveOverride}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <h4>
                                            <i className="pi pi-shield" />
                                            Request Override
                                        </h4>
                                        <textarea placeholder="Reason this pickup should be released unpaid..." value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
                                        <Button
                                            label={submitting ? "Requesting..." : "Request Override Approval"}
                                            icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-send"}
                                            severity="warning"
                                            disabled={submitting || viewOnly}
                                            onClick={handleRequestOverride}
                                        />
                                    </>
                                )}
                            </div>
                            </>
                        );
                    })()}
                </div>
            )}

            {step === "waiting" && (
                <div className={styles.sectionCard}>
                    <div className={styles.waitingState}>
                        <div className={styles.waitingIcon}>
                            <i className="pi pi-clock" />
                        </div>
                        <h4>Waiting on Admin Approval</h4>
                        <p>An override request has been sent to admins for pickup {pickup.pickupId}. This page will update automatically once it's decided.</p>
                    </div>
                </div>
            )}

            {step === "receiver" && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-user-edit" />
                            Receiver
                        </h3>
                    </div>
                    <ReceiverSelect clientId={pickup.clientId?._id || pickup.clientId} clientName={clientName} onChange={setReceiverState} />
                    <div className={styles.navRow}>
                        <span />
                        <Button
                            label={submitting ? "Completing..." : "Complete Pickup"}
                            icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                            className={styles.primaryButton}
                            disabled={submitting || !receiverState.isValid || viewOnly}
                            onClick={handleComplete}
                        />
                    </div>
                </div>
            )}

            {step === "complete" && (
                <div className={styles.sectionCard}>
                    <div className={styles.completeSummary}>
                        <div className={styles.completeIcon}>
                            <i className="pi pi-check" />
                        </div>
                        <h4>Pickup Completed</h4>
                        <p>
                            {pickup.pickupId} - {pickup.scannedPackageIds?.length || 0} package(s) released
                        </p>
                    </div>

                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-box" />
                            Packages
                        </h3>
                    </div>
                    <div className={styles.scannedList}>
                        {(pickup.scannedPackageIds || []).map((pkg) => (
                            <div key={pkg._id} className={styles.scannedItem}>
                                <strong>{pkg.Waybill}</strong>
                                <span>{pkg.packageStatus}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-user" />
                            Receiver
                        </h3>
                    </div>
                    <div className={styles.invoiceList}>
                        <div className={styles.invoiceRow}>
                            <strong>{pickup.receiver?.name || "-"}</strong>
                            <span>
                                {RECEIVER_TYPE_LABELS[pickup.receiver?.type] || pickup.receiver?.type || "-"}
                                {pickup.receiver?.type === "agent" && pickup.receiver?.idLast6 ? ` - ID ...${pickup.receiver.idLast6}` : ""}
                            </span>
                        </div>
                    </div>
                    {pickup.signatureUrl && (
                        <div className={styles.photoUploadRow}>
                            <img src={pickup.signatureUrl} alt="Receiver signature" className={styles.photoThumb} style={{ width: 160, height: 90, objectFit: "contain", background: "#fff" }} />
                        </div>
                    )}

                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-wallet" />
                            Payment
                        </h3>
                    </div>
                    {pickup.override?.status === "approved" ? (
                        <div className={styles.invoiceList}>
                            <div className={styles.invoiceRow}>
                                <strong>Released via admin override</strong>
                                <span>
                                    {pickup.override.approvedBy?.firstName} {pickup.override.approvedBy?.lastName}
                                </span>
                            </div>
                            <div className={styles.invoiceRow}>
                                <strong>Reason</strong>
                                <span>{pickup.override.reason}</span>
                            </div>
                        </div>
                    ) : pickup.paymentRecord?.method ? (
                        <div className={styles.invoiceList}>
                            <div className={styles.invoiceRow}>
                                <strong>Method</strong>
                                <span>{PAYMENT_METHOD_LABELS[pickup.paymentRecord.method] || pickup.paymentRecord.method}</span>
                            </div>
                            {pickup.paymentRecord.methodDetail && (
                                <div className={styles.invoiceRow}>
                                    <strong>Detail</strong>
                                    <span>{pickup.paymentRecord.methodDetail}</span>
                                </div>
                            )}
                            {pickup.paymentRecord.paymentOwner && (
                                <div className={styles.invoiceRow}>
                                    <strong>Paid By</strong>
                                    <span>{pickup.paymentRecord.paymentOwner}</span>
                                </div>
                            )}
                            {pickup.paymentRecord.recordedBy && (
                                <div className={styles.invoiceRow}>
                                    <strong>Recorded By</strong>
                                    <span>
                                        {pickup.paymentRecord.recordedBy?.firstName} {pickup.paymentRecord.recordedBy?.lastName}
                                    </span>
                                </div>
                            )}
                            <div className={styles.invoiceRow}>
                                <strong>Paid At</strong>
                                <span>{pickup.paymentRecord.paidAt ? new Date(pickup.paymentRecord.paidAt).toLocaleString() : "-"}</span>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.invoiceList}>
                            <div className={styles.invoiceRow}>
                                <strong>No payment was due</strong>
                                <span>-</span>
                            </div>
                        </div>
                    )}
                    {pickup.paymentRecord?.proofImageUrl && (
                        <div className={styles.photoUploadRow}>
                            <img src={pickup.paymentRecord.proofImageUrl} alt="Payment proof" className={styles.photoThumb} style={{ width: 90, height: 90 }} />
                        </div>
                    )}
                </div>
            )}
            <InvoiceConflictDialog visible={showInvoiceConflicts} conflicts={invoiceConflicts} mode="generate" onRemoveAndContinue={handleProceedDespiteConflicts} onCancel={handleCancelInvoiceConflicts} />
        </div>
    );
};

export default InStorePickupDetail;
